import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { InventoryItem, InventoryLocation } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { SearchSelect } from '../../shared/ui/SearchSelect';

type Loan = {
  id: string; item: { id: string; name: string; unit: string }; location: { id: string; name: string }; quantity: number; borrower: string | null; purpose: string | null;
  dueOn: string | null; conditionOut: string | null; conditionIn: string | null; lentAt: string; returnedAt: string | null; overdue: boolean;
};
const day = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

/** Préstamos: quién se llevó qué, para cuándo lo regresa y en qué estado. */
export function LoansCard({ churchId, items, locations, canManage, onChange }: { churchId: string; items: InventoryItem[]; locations: InventoryLocation[]; canManage: boolean; onChange: () => void }) {
  const base = `/inventory/churches/${churchId}/loans`;
  const data = useAsync((signal) => api.get<{ items: Loan[] }>(base, signal).then((r) => r.items), [base]);
  const people = useAsync((signal) => (canManage ? api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items).catch(() => []) : Promise.resolve([])), [churchId, canManage]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.status !== 'ready') return null;
  const active = data.data.filter((l) => !l.returnedAt);
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      onChange();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={`Préstamos${active.length ? ` (${active.length} fuera)` : ''}`} action={canManage && !open ? <Button variant="ghost" onClick={() => setOpen(true)}>Prestar</Button> : null}>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {open ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const t = (k: string) => String(f.get(k) ?? '').trim() || null;
            void run(() => api.post(base, {
              itemId: t('item'), locationId: t('location'), quantity: Number(t('quantity') ?? 1), borrowerId: t('person'), borrowerName: t('person') ? null : t('name'),
              purpose: t('purpose'), dueOn: t('due'), conditionOut: t('condition'),
            }), 'Préstamo registrado.').then((ok) => ok && setOpen(false));
          }}
        >
          <div className="form__row form__row--3">
            <div className="field"><label htmlFor="ln-item">Artículo</label><select id="ln-item" name="item">{items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
            <div className="field"><label htmlFor="ln-loc">De dónde</label><select id="ln-loc" name="location">{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
            <Field label="Cantidad" name="quantity" type="number" min={1} step="any" defaultValue={1} />
          </div>
          <div className="form__row">
            <div className="field">
              <label htmlFor="ln-person">Se lo lleva (miembro)</label>
              <SearchSelect
                id="ln-person"
                name="person"
                emptyLabel="— otra persona —"
                options={(people.status === 'ready' ? people.data : []).map((p) => ({ value: p.personId, label: `${p.givenName} ${p.familyName}`.trim() }))}
              />
            </div>
            <Field label="…o nombre" name="name" maxLength={120} />
          </div>
          <div className="form__row form__row--3">
            <Field label="Para qué" name="purpose" maxLength={300} />
            <Field label="Lo regresa el" name="due" type="date" />
            <Field label="Cómo sale" name="condition" maxLength={300} placeholder="En buen estado" />
          </div>
          <div className="actions"><Button type="submit" busy={busy}>Prestar</Button><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button></div>
        </form>
      ) : null}
      {data.data.length === 0 ? <p className="muted small">No hay préstamos.</p> : null}
      <ul className="list">
        {data.data.map((l) => (
          <li key={l.id} className="list__item list__item--stack">
            <span>
              <strong>{l.quantity} {l.item.unit} · {l.item.name}</strong> → {l.borrower ?? '—'}
              <span className="small muted"> · desde {day(l.lentAt)}{l.dueOn ? ` · regresa el ${day(l.dueOn)}` : ''}{l.purpose ? ` · ${l.purpose}` : ''}{l.conditionOut ? ` · salió: ${l.conditionOut}` : ''}</span>
              {l.returnedAt ? <span className="small"> · regresó el {day(l.returnedAt)}{l.conditionIn ? ` (${l.conditionIn})` : ''}</span> : l.overdue ? <span className="text-danger small"> · vencido</span> : null}
            </span>
            {canManage && !l.returnedAt ? (
              <form className="family-add" onSubmit={(e) => { e.preventDefault(); const c = String(new FormData(e.currentTarget).get('cond') ?? '').trim() || null; void run(() => api.post(`${base}/${l.id}/return`, { conditionIn: c }), 'Regresado.'); }}>
                <Field label="Cómo regresó" name="cond" maxLength={300} />
                <Button type="submit" variant="secondary" busy={busy}>Recibir</Button>
              </form>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}
